<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  selectedOfferDecisionBlockedReason: { type: String, default: '' },
  offerAction: { type: String, default: '' },
  canEdit: { type: Boolean, default: false },
  canCounter: { type: Boolean, default: false },
  canWithdraw: { type: Boolean, default: false },
  canDecline: { type: Boolean, default: false },
  canAccept: { type: Boolean, default: false },
});

const emit = defineEmits([
  'close',
  'edit',
  'counter',
  'withdraw',
  'decline',
  'accept',
]);

const mobileSecondaryActionCount = computed(
  () =>
    [props.canEdit, props.canCounter, props.canWithdraw].filter(Boolean).length
);

const closeOfferDetails = () => emit('close');
const openCounterModal = () => emit('counter');
const openDeclineModal = () => emit('decline');
const openAcceptModal = () => emit('accept');
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="offer-details-footer-actions grid gap-3">
    <div class="min-w-0">
      <p
        class="text-[11px] font-semibold uppercase tracking-[0.14em] text-lais-text-muted"
      >
        Ações da proposta
      </p>
      <p
        v-if="selectedOfferDecisionBlockedReason"
        role="status"
        aria-live="polite"
        class="mt-1 max-w-2xl break-words text-left text-[12px] leading-5 text-lais-text-muted"
      >
        {{ selectedOfferDecisionBlockedReason }}
      </p>
    </div>
    <div class="offer-details-footer-actions__compact grid gap-2">
      <LaisPrimaryButton
        v-if="canAccept"
        class="offer-details-footer-actions__accept w-full whitespace-normal text-center"
        icon="i-lucide-check"
        :is-loading="offerAction === 'accept'"
        @click="openAcceptModal"
      >
        Aceitar proposta
      </LaisPrimaryButton>
      <LaisPrimaryButton
        class="w-full whitespace-normal text-center"
        variant="ghost"
        @click="closeOfferDetails"
      >
        Fechar
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canDecline"
        class="w-full whitespace-normal text-center"
        variant="ghost"
        icon="i-lucide-x"
        :is-loading="offerAction === 'decline'"
        @click="openDeclineModal"
      >
        Recusar
      </LaisPrimaryButton>

      <template v-if="mobileSecondaryActionCount === 1">
        <LaisPrimaryButton
          v-if="canEdit"
          class="w-full whitespace-normal text-center"
          variant="ghost"
          icon="i-lucide-pencil"
          :is-loading="offerAction === 'edit'"
          @click="emit('edit')"
        >
          Editar proposta
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="canCounter"
          class="w-full whitespace-normal text-center"
          variant="ghost"
          icon="i-lucide-git-compare"
          :is-loading="offerAction === 'counter'"
          @click="openCounterModal"
        >
          Fazer contraproposta
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="canWithdraw"
          class="w-full whitespace-normal text-center"
          variant="ghost"
          icon="i-lucide-undo-2"
          :is-loading="offerAction === 'withdraw'"
          @click="emit('withdraw')"
        >
          Retirar proposta
        </LaisPrimaryButton>
      </template>

      <details
        v-else-if="mobileSecondaryActionCount > 1"
        class="offer-details-footer-actions__more min-w-0 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-2"
      >
        <summary
          class="flex min-h-10 cursor-pointer list-none items-center justify-between gap-3 rounded-lais px-3 py-2 text-[13px] font-semibold text-lais-text [&::-webkit-details-marker]:hidden"
        >
          <span>Mais ações</span>
          <span class="i-lucide-chevron-down size-4" aria-hidden="true" />
        </summary>
        <div class="offer-details-footer-actions__more-grid grid gap-2 pt-2">
          <LaisPrimaryButton
            v-if="canEdit"
            class="w-full whitespace-normal text-center"
            variant="ghost"
            icon="i-lucide-pencil"
            :is-loading="offerAction === 'edit'"
            @click="emit('edit')"
          >
            Editar proposta
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="canCounter"
            class="w-full whitespace-normal text-center"
            variant="ghost"
            icon="i-lucide-git-compare"
            :is-loading="offerAction === 'counter'"
            @click="openCounterModal"
          >
            Fazer contraproposta
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="canWithdraw"
            class="w-full whitespace-normal text-center"
            variant="ghost"
            icon="i-lucide-undo-2"
            :is-loading="offerAction === 'withdraw'"
            @click="emit('withdraw')"
          >
            Retirar proposta
          </LaisPrimaryButton>
        </div>
      </details>
    </div>

    <div class="offer-details-footer-actions__wide gap-2">
      <LaisPrimaryButton
        class="w-full whitespace-normal text-center"
        variant="ghost"
        @click="closeOfferDetails"
      >
        Fechar
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canEdit"
        class="w-full whitespace-normal text-center"
        variant="ghost"
        icon="i-lucide-pencil"
        :is-loading="offerAction === 'edit'"
        @click="emit('edit')"
      >
        Editar proposta
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canCounter"
        class="w-full whitespace-normal text-center"
        variant="ghost"
        icon="i-lucide-git-compare"
        :is-loading="offerAction === 'counter'"
        @click="openCounterModal"
      >
        Fazer contraproposta
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canWithdraw"
        class="w-full whitespace-normal text-center"
        variant="ghost"
        icon="i-lucide-undo-2"
        :is-loading="offerAction === 'withdraw'"
        @click="emit('withdraw')"
      >
        Retirar proposta
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canDecline"
        class="w-full whitespace-normal text-center"
        variant="ghost"
        icon="i-lucide-x"
        :is-loading="offerAction === 'decline'"
        @click="openDeclineModal"
      >
        Recusar
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canAccept"
        class="w-full whitespace-normal text-center"
        icon="i-lucide-check"
        :is-loading="offerAction === 'accept'"
        @click="openAcceptModal"
      >
        Aceitar proposta
      </LaisPrimaryButton>
    </div>
  </div>
</template>

<style scoped>
.offer-details-footer-actions {
  container-type: inline-size;
}

.offer-details-footer-actions__wide {
  display: none;
}

@container (min-width: 24rem) {
  .offer-details-footer-actions__compact {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .offer-details-footer-actions__accept {
    grid-column: 1 / -1;
  }

  .offer-details-footer-actions__more-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (min-width: 48rem) {
  .offer-details-footer-actions {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
  }

  .offer-details-footer-actions__compact {
    display: none;
  }

  .offer-details-footer-actions__wide {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .offer-details-footer-actions__wide :deep(button) {
    width: auto;
  }
}
</style>
